import { prepareIdentityStorage } from './identity-storage-bootstrap.js';
import {
  AUTH_EVENTS,
  acceptInvite,
  handleAuthCallback,
  login,
  logout,
  onAuthChange,
  recoverPassword,
  requestPasswordRecovery,
  type User,
} from '@netlify/identity';
import GoTrue from 'gotrue-js';
import { API_ROUTES, type AccountResponse } from '@shared/api-contract.js';
import type { AccountProfile } from '@shared/account-types.js';

export const ACCOUNT_AUTH_CHANGED_EVENT = 'momo-account-auth-changed';

const ACCOUNT_ERROR_KEY = 'momo-account-auth-error';
/** @deprecated Cleared on init; recovery now stores the token itself. */
const LEGACY_PASSWORD_RESET_KEY = 'momo-password-reset';
const RECOVERY_TOKEN_KEY = 'momo-recovery-token';
const INVITE_TOKEN_KEY = 'momo-invite-token';
const AUTH_HASH_PATTERN = /^#(confirmation_token|recovery_token|invite_token|email_change_token|access_token)=/;
const IDENTITY_API_URL = '/.netlify/identity';

let initialized = false;
let initPromise: Promise<void> | null = null;
let currentAccount: AccountProfile | null = null;
let currentUser: User | null = null;
let goTrueClient: GoTrue | null = null;

function dispatchAuthChanged(): void {
  window.dispatchEvent(new CustomEvent(ACCOUNT_AUTH_CHANGED_EVENT));
}

function setAccountError(message: string | null): void {
  try {
    if (message) {
      sessionStorage.setItem(ACCOUNT_ERROR_KEY, message);
    } else {
      sessionStorage.removeItem(ACCOUNT_ERROR_KEY);
    }
  } catch {
    // Ignore storage errors; the UI will still fall back to generic copy.
  }
}

export function getAccountAuthError(): string | null {
  try {
    return sessionStorage.getItem(ACCOUNT_ERROR_KEY);
  } catch {
    return null;
  }
}

function clearHash(): void {
  if (typeof window === 'undefined') return;
  history.replaceState(null, '', window.location.pathname + window.location.search);
}

function setRecoveryToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(RECOVERY_TOKEN_KEY, token);
    else sessionStorage.removeItem(RECOVERY_TOKEN_KEY);
    sessionStorage.removeItem(LEGACY_PASSWORD_RESET_KEY);
  } catch {
    // Ignore storage errors.
  }
}

function getRecoveryToken(): string | null {
  try {
    return sessionStorage.getItem(RECOVERY_TOKEN_KEY);
  } catch {
    return null;
  }
}

function setInviteToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(INVITE_TOKEN_KEY, token);
    else sessionStorage.removeItem(INVITE_TOKEN_KEY);
  } catch {
    // Ignore storage errors.
  }
}

export function needsPasswordReset(): boolean {
  return Boolean(getRecoveryToken());
}

export function getPendingInviteToken(): string | null {
  try {
    return sessionStorage.getItem(INVITE_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function shouldShowAccountGate(): boolean {
  return needsPasswordReset() || Boolean(getPendingInviteToken());
}

/**
 * Session source of truth for this SPA.
 *
 * Do not use `@netlify/identity` getUser() here: it requires an `nf_jwt` cookie and
 * clears the GoTrue localStorage session when that cookie is missing (common when
 * the JWT exceeds browser cookie size limits). Bearer tokens via user.jwt() are
 * what our Netlify Functions already expect.
 */
function getGoTrueClient(): GoTrue {
  if (!goTrueClient) {
    const fromEnv = import.meta.env.VITE_NETLIFY_IDENTITY_URL?.trim();
    const apiUrl = fromEnv && !/^https?:\/\//i.test(fromEnv) ? fromEnv : IDENTITY_API_URL;
    goTrueClient = new GoTrue({ APIUrl: apiUrl, setCookie: false });
  }
  return goTrueClient;
}

function mapGoTrueUser(user: {
  id: string;
  email?: string;
  role?: string;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
  confirmed_at?: string | null;
  created_at?: string;
  updated_at?: string;
}): User {
  const appMetadata = user.app_metadata ?? {};
  const userMetadata = user.user_metadata ?? {};
  const name = userMetadata.full_name ?? userMetadata.name;
  const pictureUrl = userMetadata.avatar_url;
  const roles = Array.isArray(appMetadata.roles)
    ? appMetadata.roles.filter((role): role is string => typeof role === 'string')
    : undefined;

  return {
    id: user.id,
    email: user.email,
    role: user.role || undefined,
    confirmedAt: user.confirmed_at || undefined,
    createdAt: user.created_at,
    updatedAt: user.updated_at,
    provider: typeof appMetadata.provider === 'string'
      ? appMetadata.provider as User['provider']
      : undefined,
    name: typeof name === 'string' ? name : undefined,
    pictureUrl: typeof pictureUrl === 'string' ? pictureUrl : undefined,
    roles,
    userMetadata,
    appMetadata,
  };
}

function readGoTrueSessionUser(): User | null {
  if (typeof window === 'undefined') return null;
  const gotrueUser = getGoTrueClient().currentUser();
  return gotrueUser ? mapGoTrueUser(gotrueUser) : null;
}

async function syncCurrentUser(): Promise<User | null> {
  currentUser = readGoTrueSessionUser();
  return currentUser;
}

/**
 * Capture recovery_token without redeeming it yet.
 * handleAuthCallback() would call recover() immediately and leave Save password
 * without a reliable session.
 */
function captureRecoveryTokenFromHash(): boolean {
  if (typeof window === 'undefined') return false;
  if (!window.location.hash.includes('recovery_token=')) return false;

  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get('recovery_token');
  if (!token) return false;

  setRecoveryToken(token);
  setAccountError(null);
  clearHash();
  return true;
}

async function processAuthCallback(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!AUTH_HASH_PATTERN.test(window.location.hash)) return;

  if (captureRecoveryTokenFromHash()) return;

  const result = await handleAuthCallback();
  if (!result) return;

  if (result.type === 'invite' && result.token) {
    setInviteToken(result.token);
    setAccountError(null);
    return;
  }

  // confirmation / oauth / email_change — session is ready in GoTrue storage
  setAccountError(null);
  currentUser = readGoTrueSessionUser() ?? result.user;
}

export function initAccountAuth(): Promise<void> {
  if (initialized) return initPromise ?? Promise.resolve();
  initialized = true;
  prepareIdentityStorage();

  initPromise = (async () => {
    try {
      sessionStorage.removeItem(LEGACY_PASSWORD_RESET_KEY);
    } catch {
      // Ignore storage errors.
    }

    onAuthChange((event, user) => {
      // Prefer GoTrue storage — identity events can report a user even when
      // cookies (and thus getUser) would disagree.
      currentUser = readGoTrueSessionUser() ?? user;
      if (event === AUTH_EVENTS.LOGOUT) {
        currentUser = null;
        currentAccount = null;
      }
      dispatchAuthChanged();
    });

    try {
      await processAuthCallback();
    } catch (err) {
      setAccountError(err instanceof Error ? err.message : 'Auth callback failed');
    }

    await syncCurrentUser();
    dispatchAuthChanged();
  })();

  return initPromise;
}

export function getCurrentIdentityUser(): User | null {
  void initAccountAuth();
  return currentUser ?? readGoTrueSessionUser();
}

export function getCurrentAccount(): AccountProfile | null {
  return currentAccount;
}

export async function getIdentityToken(forceRefresh = false): Promise<string | null> {
  await initAccountAuth();
  const gotrueUser = getGoTrueClient().currentUser();
  if (!gotrueUser) return null;

  const details = gotrueUser.tokenDetails();
  if (!details?.access_token) return null;

  // gotrue-js stores expires_at in ms; tolerate seconds just in case.
  const expiresAtMs = details.expires_at < 1e12 ? details.expires_at * 1000 : details.expires_at;
  const expired = expiresAtMs <= Date.now();
  const nearExpiry = expiresAtMs - 60_000 <= Date.now();

  if (forceRefresh || nearExpiry) {
    try {
      return await gotrueUser.jwt(true);
    } catch (err) {
      console.warn('[account-auth] Identity token refresh failed', err);
      return expired ? null : details.access_token;
    }
  }

  return details.access_token;
}

export async function withIdentityHeaders(headers?: HeadersInit): Promise<Headers> {
  const next = new Headers(headers);
  const token = await getIdentityToken();
  if (token) {
    next.set('Authorization', `Bearer ${token}`);
  }
  return next;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return {};
  }
}

function errorFromBody(data: unknown, fallback: string): string {
  const body = data as { error?: unknown; message?: unknown };
  if (typeof body.error === 'string') return body.error;
  if (typeof body.message === 'string') return body.message;
  return fallback;
}

export function toAuthErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return fallback;
}

export async function fetchAccountStatus(): Promise<AccountProfile> {
  const response = await fetch(API_ROUTES.account, {
    headers: await withIdentityHeaders(),
  });
  const data = await readJson(response) as Partial<AccountResponse> & { error?: string };

  if (!response.ok || !data.account) {
    const message = errorFromBody(data, 'Failed to load account status');
    setAccountError(message);
    throw new Error(message);
  }

  currentAccount = data.account;
  setAccountError(null);
  return currentAccount;
}

export async function ensureAccountAuthenticated(): Promise<boolean> {
  await initAccountAuth();
  if (shouldShowAccountGate()) {
    return false;
  }

  if (!(await syncCurrentUser())) {
    currentAccount = null;
    return false;
  }

  try {
    await fetchAccountStatus();
    return true;
  } catch {
    currentAccount = null;
    return false;
  }
}

export async function loginAccount(email: string, password: string): Promise<User> {
  await initAccountAuth();
  const user = await login(email.trim(), password);
  // Re-read from GoTrue storage rather than trusting cookie-backed getUser().
  currentUser = readGoTrueSessionUser() ?? user;
  setRecoveryToken(null);
  setInviteToken(null);
  setAccountError(null);
  dispatchAuthChanged();
  return currentUser;
}

/** Leave an in-progress reset/invite gate and return to the sign-in form. */
export async function cancelAccountAuthGate(): Promise<void> {
  await initAccountAuth();
  setRecoveryToken(null);
  setInviteToken(null);
  setAccountError(null);
  if (getGoTrueClient().currentUser()) {
    try {
      await logout();
    } catch {
      // Still clear local gate state below.
    }
  }
  currentUser = null;
  currentAccount = null;
  dispatchAuthChanged();
}

export async function requestAccountPasswordRecovery(email: string): Promise<void> {
  await initAccountAuth();
  await requestPasswordRecovery(email.trim());
}

export async function completeAccountPasswordReset(password: string): Promise<User> {
  await initAccountAuth();
  const token = getRecoveryToken();
  if (!token) {
    throw new Error('Recovery link expired. Request a new password reset email.');
  }

  const recovered = await recoverPassword(token, password);

  // Establish a normal GoTrue login session with the new password.
  let user = recovered;
  if (recovered.email) {
    try {
      user = await login(recovered.email, password);
    } catch {
      user = recovered;
    }
  }

  currentUser = readGoTrueSessionUser() ?? user;
  setRecoveryToken(null);
  setAccountError(null);
  dispatchAuthChanged();
  return currentUser;
}

export async function acceptAccountInvite(token: string, password: string): Promise<User> {
  await initAccountAuth();
  const user = await acceptInvite(token, password);
  currentUser = readGoTrueSessionUser() ?? user;
  setInviteToken(null);
  setAccountError(null);
  dispatchAuthChanged();
  return currentUser;
}

export async function logoutAccount(): Promise<void> {
  await initAccountAuth();
  try {
    await logout();
  } finally {
    try {
      getGoTrueClient().currentUser()?.clearSession();
    } catch {
      // Ignore.
    }
    currentUser = null;
    currentAccount = null;
    setRecoveryToken(null);
    setInviteToken(null);
    setAccountError(null);
    dispatchAuthChanged();
    window.location.reload();
  }
}

export function redirectToAccountAuth(message: string): void {
  setAccountError(message);
  window.location.reload();
}

async function postBillingSession(route: string): Promise<string> {
  const response = await fetch(route, {
    method: 'POST',
    headers: await withIdentityHeaders({ 'Content-Type': 'application/json' }),
  });
  const data = await readJson(response) as { url?: string; error?: string };

  if (!response.ok || typeof data.url !== 'string') {
    throw new Error(errorFromBody(data, 'Billing request failed'));
  }
  return data.url;
}

/** Redirect to Stripe Checkout for the monthly Member plan. */
export async function startCheckoutSession(): Promise<void> {
  const url = await postBillingSession(API_ROUTES.createCheckoutSession);
  window.location.assign(url);
}

/** Redirect to Stripe Customer Portal to manage billing. */
export async function startPortalSession(): Promise<void> {
  const url = await postBillingSession(API_ROUTES.createPortalSession);
  window.location.assign(url);
}

/**
 * After Checkout success, force a token refresh and re-check membership.
 * Webhooks may take a moment to update Identity app_metadata.
 */
export async function refreshAccountAfterBilling(): Promise<boolean> {
  await getIdentityToken(true);
  for (let attempt = 0; attempt < 6; attempt += 1) {
    try {
      await fetchAccountStatus();
      return true;
    } catch {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await getIdentityToken(true);
    }
  }
  return false;
}
